import React, { useEffect, useState } from "react"
import styles from "./dashboard.module.css"
import Card from "./Card"
import BreakForm from "../../components/Form/BreakForm/index"
import Select from "../../components/Form/Select"
import Button from "../../components/Form/Button"
import FormGroupInline from "../../components/Form/FormGroupInline"
import DatePicker from "../../components/Form/DatePicker"
import Checkbox from "../../components/Form/Checkbox"
import Loading from "../layouts/Loading/index"
import Chart from "./Charts/Chart"
import { useData } from "../../contexts/dataContext"
import api from "../../services/api"
import { areObjectsEqual, getUsersBySelectedTeams, getValidUserIdsByTeams } from "../../utils/utils"
import moment from "moment"
import { toast } from "react-toastify"
import imageFilterDash from "../../images/filter-dash.png"
import BackgroundSelectFilter from "../../components/BackgroundSelectFilter"
import Table from "../../components/Table/index"
import { useWebSocketChatContext } from "../../contexts/webSocketChatContext"
import { TfiReload } from "react-icons/tfi"
import { useAuth } from "../../contexts/authContext"
import PdfExportButton from "./PdfExportButton"
import { formatPeriod, getOptionLabels } from "../../utils/dashboardPdf"

const initialFilters = {
    dateStart: moment().subtract(14, "d").format("YYYY-MM-DD"),
    dateEnd: moment().subtract(1, "d").format("YYYY-MM-DD"),
    includeUnassigned: false,
}

const DashboardChat = () => {
    const { teams, roles, users } = useData()
    const { chatInboxes } = useWebSocketChatContext()
    const { permissions, account, user } = useAuth()
    const hasFullChatAccess = permissions?.chat > 1

    const [data, setData] = useState({})
    const [filters, setFilters] = useState(initialFilters)
    const [filtersTemp, setFiltersTemp] = useState(initialFilters)
    const [changedFilters, setChangedFilters] = useState(false)
    const [loading, setLoading] = useState(false)
    const [backgroundFilter, setBackgroundFilter] = useState(true)
    const [aiAgents, setAiAgents] = useState([])

    let isLoadingDashboard = false

    useEffect(() => {
        handleFiltersChanged()
    }, [filtersTemp])

    useEffect(() => {
        api.get("/chat/ai-agents")
            .then(response => setAiAgents((response?.data?.data || []).map(agent => ({
                id: agent._id,
                value: agent.name || "Agente de IA",
            }))))
            .catch(() => setAiAgents([]))
    }, [])

    const getDataByFilter = async (updateFilters = false) => {
        try {
            if (!isLoadingDashboard) {
                isLoadingDashboard = true
                setLoading(true)

                const queryString = new URLSearchParams(filtersTemp)?.toString()
                const response = await api.get(`/chat/dashboard?${queryString}`)
                const responseData = response.data?.data

                setData(responseData)

                if (updateFilters) {
                    setFilters(filtersTemp)
                }

                setChangedFilters(false)
                setBackgroundFilter(false)
            }
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setLoading(false)
            isLoadingDashboard = false
        }
    }

    const handleChangeFilters = (name, value) => {
        setFiltersTemp(previousValue => ({
            ...previousValue,
            [name]: value,
            ...(name === "teams" ? { users: getValidUserIdsByTeams(previousValue.users, value, users, teams, [], previousValue.teams) } : {}),
        }))
    }

    const handleFiltersChanged = () => {
        setChangedFilters(!areObjectsEqual(filters, filtersTemp))
    }

    const handleChangePeriod = (dateStart, dateEnd) => {
        handleChangeFilters("dateStart", dateStart)
        handleChangeFilters("dateEnd", dateEnd)
    }

    const reportFilters = [
        { label: "Período", value: formatPeriod(filters.dateStart, filters.dateEnd) },
        { label: "Caixas de entrada", value: getOptionLabels(filters.inboxes, chatInboxes?.map(inbox => ({ id: inbox._id, value: inbox.name }))) },
        { label: "Equipes", value: getOptionLabels(filters.teams, teams) },
        { label: "Cargos", value: getOptionLabels(filters.roles, roles) },
        { label: "Usuários", value: getOptionLabels(filters.users, users) },
        { label: "Agentes de IA", value: getOptionLabels(filters.aiAgents, aiAgents) },
        ...(hasFullChatAccess ? [{ label: "Incluir sem responsável", value: filters.includeUnassigned ? "Sim" : "Não" }] : []),
    ]

    return (
        <div className={styles.container}>
            <div className={styles.filters}>
                <div className={styles.groupLeft}>
                    <Select
                        name="Caixa de Entrada"
                        placeholder="Selecione os canais"
                        options={chatInboxes?.map(inbox => ({ id: inbox._id, value: inbox.name })) || []}
                        selected={filtersTemp.inboxes || []}
                        onChange={value => handleChangeFilters("inboxes", value)}
                        multiple={true}
                    />
                    <DatePicker
                        name="Período"
                        dateStart={filtersTemp.dateStart}
                        dateEnd={filtersTemp.dateEnd}
                        onChange={handleChangePeriod}
                        multiple={true}
                    />
                    <Select
                        name="Equipes"
                        placeholder="Pesquise uma equipe"
                        options={teams || []}
                        selected={filtersTemp.teams || []}
                        onChange={value => handleChangeFilters("teams", value)}
                        multiple={true}
                    />
                    <Select
                        name="Cargos"
                        placeholder="Pesquise um cargo"
                        options={roles || []}
                        selected={filtersTemp.roles || []}
                        onChange={value => handleChangeFilters("roles", value)}
                        multiple={true}
                    />
                    <Select
                        name="Usuários"
                        placeholder="Pesquise um usuário"
                        options={getUsersBySelectedTeams(filtersTemp.teams, users, teams)}
                        selected={filtersTemp.users || []}
                        onChange={value => handleChangeFilters("users", value)}
                        multiple={true}
                    />
                    <Select
                        name="Agentes de IA"
                        placeholder="Selecione os agentes"
                        options={aiAgents}
                        selected={filtersTemp.aiAgents || []}
                        onChange={value => handleChangeFilters("aiAgents", value)}
                        multiple={true}
                    />
                    {hasFullChatAccess && (
                        <button
                            type="button"
                            className={styles.checkFilter}
                            onClick={() => handleChangeFilters("includeUnassigned", !filtersTemp.includeUnassigned)}
                        >
                            <Checkbox checked={filtersTemp.includeUnassigned} />
                            <span>Sem responsável</span>
                        </button>
                    )}
                </div>
                <div className={styles.groupRight}>
                    <PdfExportButton
                        title="Dashboard do Chat"
                        accountName={account?.company?.fantasy_name || account?.name}
                        generatedBy={user?.name}
                        filters={reportFilters}
                        data={data}
                        disabled={changedFilters || backgroundFilter || loading}
                    />
                    {!(changedFilters || backgroundFilter) &&
                        <div className={styles.reload} onClick={() => getDataByFilter(true)}>
                            <TfiReload />
                        </div>
                    }
                </div>
            </div>
            <div className={styles.filterHelp}>
                <strong>Como os filtros do Chat são aplicados:</strong>
                <span>Equipes e cargos retornam apenas conversas atribuídas por equipe/cargo.</span>
                <span>Usuários retornam apenas conversas atribuídas diretamente aos usuários selecionados.</span>
                <span>Agentes de IA filtram os indicadores e a tabela de desempenho automatizado.</span>
                {hasFullChatAccess && <span>Use “Sem responsável” para incluir conversas sem atribuição registrada.</span>}
                <span>{hasFullChatAccess ? "Seu acesso permite visualizar métricas de todos os responsáveis disponíveis." : "Seu acesso mostra somente métricas dentro da sua propriedade no Chat."}</span>
            </div>
            <div className={styles.body}>
                {(changedFilters || backgroundFilter) && (
                    <div className={styles.changedFilters}>
                        <FormGroupInline>
                            <Button variant="secondary" onClick={() => getDataByFilter(true)}>
                                Aplicar Filtros
                            </Button>
                            <Button variant="primary" onClick={() => setFiltersTemp(filters)}>
                                Limpar
                            </Button>
                        </FormGroupInline>
                    </div>
                )}
                {!backgroundFilter && (
                    <>
                        <div className={styles.cards}>
                            {data?.cards?.length > 0 && data.cards.map(item => (
                                <Card
                                    key={item.id}
                                    name={item.name}
                                    value={item.value}
                                    variation={item.variation}
                                    is_positive={item.is_positive}
                                    description={item.description}
                                    tooltip={item.tooltip || item.description}
                                />
                            ))}
                        </div>
                        <div className={styles.charts}>
                            {data?.charts?.map(item => (
                                <Chart
                                    key={item.id}
                                    data={item}
                                />
                            ))}
                        </div>
                        {data?.tables?.map(item => (
                            <div key={item.id} className={styles.groupTable}>
                                <BreakForm title={item.title} border={false} showAction={false} />
                                <Table
                                    className={styles.table}
                                    headers={item.headers}
                                    values={item.values}
                                    messageNotRegisters="Nenhum dado para exibir"
                                />
                            </div>
                        ))}
                    </>
                )}
                {backgroundFilter && (
                    <BackgroundSelectFilter
                        text="Selecione os filtros desejados e aplique para visualizar os dados!"
                        image={imageFilterDash}
                    />
                )}
            </div>
            {loading && <Loading fullPage={true} />}
        </div>
    )
}

export default DashboardChat
